/**
 * Delegate - multicast and singlecast callback handlers for game scripts.
 */

export type Callback<T extends any[] = any[], R = void> = (...args: T) => R;

/**
 * Multicast delegate that can invoke multiple callbacks.
 *
 * @template T - Argument types
 * @template R - Return type
 */
export class Delegate<T extends any[] = any[], R = void> {
  private callbacks: Callback<T, R>[] = [];

  /** Adds a callback to the delegate. */
  public add(callback: Callback<T, R>): void {
    if (!this.callbacks.includes(callback)) {
      this.callbacks.push(callback);
    }
  }

  /** Removes a callback from the delegate. */
  public remove(callback: Callback<T, R>): void {
    const index = this.callbacks.indexOf(callback);
    if (index !== -1) {
      this.callbacks.splice(index, 1);
    }
  }

  /** Clears all callbacks from the delegate. */
  public clear(): void {
    this.callbacks = [];
  }

  /**
   * Invokes all registered callbacks with the given arguments.
   *
   * @param args - Arguments to pass to the callbacks
   * @returns Array of return values from each callback
   */
  public invoke(...args: T): R[] {
    return this.callbacks.map(callback => callback(...args));
  }

  /**
   * Invokes all registered callbacks with the given arguments.
   *
   * @param predicate - Optional predicate to determine if the callback should be terminated
   * @param args - Arguments to pass to the callbacks
   * @returns Array of return values from each callback
   */
  public invokeWithPredicate(predicate?: (result: R) => boolean, ...args: T): R[] {
    const results: R[] = [];

    for (let i = 0; i < this.callbacks.length; i++) {
      const result = this.callbacks[i](...args);
      results.push(result);
      if (predicate && predicate(result)) {
        break;
      }
    }
    return results;
  }
}

/**
 * Singlecast delegate that can invoke one callback.
 *
 * @template T - Argument types
 * @template R - Return type
 */
export class SinglecastDelegate<T extends any[] = any[], R = void> {
  private callback: Callback<T, R> | null = null;

  /** Sets the callback for this delegate. */
  public set(callback: Callback<T, R> | null): void {
    this.callback = callback;
  }

  /** Clears the current callback. */
  public clear(): void {
    this.callback = null;
  }

  /**
   * Invokes the registered callback with the given arguments.
   *
   * @param args - Arguments to pass to the callback
   * @returns The return value of the callback, or undefined if no callback is set
   */
  public invoke(...args: T): R | undefined {
    return this.callback?.(...args);
  }
}